import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiDownload, FiCopy, FiEye, FiRefreshCw, FiX } from 'react-icons/fi';
import { qrcodesAPI, foldersAPI } from '../services/api';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 14px 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const PageTitle = styled.h1`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CreateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: black;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s ease;
  width: fit-content;

  &:hover {
    background: #374151;
    text-decoration: none;
    color: white;
  }
`;

const QRCodeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
`;

const QRCodeCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 12px;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: black;
  }
`;

const QRCodeImage = styled.img`
  width: 100%;
  max-width: 180px;
  height: auto;
  margin: 0 auto;
  display: block;
  border-radius: 6px;
  margin-bottom: 10px;
  border: 1px solid #f3f4f6;
`;

const QRCodeTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 5px 0;
`;

const QRCodeDescription = styled.p`
  font-size: 10px;
  color: #6b7280;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const QRCodeMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 10px;
  color: #9ca3af;
`;

const QRCodeActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  border: 1px solid ${props => props.primary ? 'black' : '#e5e7eb'};
  border-radius: 4px;
  background: ${props => props.primary ? 'black' : 'white'};
  color: ${props => props.primary ? 'white' : '#6b7280'};
  font-weight: 500;
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: black;
    background: ${props => props.primary ? '#374151' : '#f9fafb'};
    color: ${props => props.primary ? 'white' : '#1f2937'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const EmptyStateTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 10px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 20px 0;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
`;

const ModalTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #1f2937;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

function QRCodeManager() {
  const [qrCodes, setQrCodes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ isOpen: false, qrCode: null });
  const [editContent, setEditContent] = useState('');
  const [editType, setEditType] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [moveModal, setMoveModal] = useState({ isOpen: false, qrCode: null });
  const [isMoving, setIsMoving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQRCodes();
    fetchFolders();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await qrcodesAPI.getAll();
      setQrCodes(response.data.data);
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      toast.error('Failed to load QR codes');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await foldersAPI.getFolders();
      setFolders(response.data.data || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      await qrcodesAPI.delete(id);
      toast.success('QR code deleted successfully');
      fetchQRCodes();
    } catch (error) {
      console.error('Error deleting QR code:', error);
      toast.error('Failed to delete QR code');
    }
  };

  const handleEdit = (qrCode) => {
    setEditContent(qrCode.content);
    setEditType(qrCode.qr_type);
    setEditModal({ isOpen: true, qrCode });
  };

  const handleUpdateContent = async () => {
    if (!editContent.trim()) {
      toast.error('Content cannot be empty');
      return;
    }

    try {
      setIsUpdating(true);
      await qrcodesAPI.updateContent(editModal.qrCode.id, editContent, editType);
      toast.success('QR code content updated successfully!');
      setEditModal({ isOpen: false, qrCode: null });
      fetchQRCodes();
    } catch (error) {
      console.error('Error updating QR code:', error);
      toast.error('Failed to update QR code content');
    } finally {
      setIsUpdating(false);
    }
  };

  const downloadQRCode = (qrCode) => {
    const link = document.createElement('a');
    link.href = qrCode.qr_code_image;
    link.download = `${qrCode.title || 'qrcode'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded successfully!');
  };

  const copyQRCode = (qrCode) => {
    fetch(qrCode.qr_code_image)
      .then(res => res.blob())
      .then(blob => {
        const item = new ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([item]).then(() => {
          toast.success('QR code copied to clipboard!');
        }).catch(() => {
          toast.error('Failed to copy QR code');
        });
      })
      .catch(() => {
        toast.error('Failed to copy QR code');
      });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleMove = (qrCode) => {
    setMoveModal({ isOpen: true, qrCode });
  };

  const handleMoveToFolder = async (folderId) => {
    if (!moveModal.qrCode) return;

    try {
      setIsMoving(true);
      await qrcodesAPI.moveToFolder(moveModal.qrCode.id, folderId);
      toast.success('QR code moved successfully!');
      setMoveModal({ isOpen: false, qrCode: null });
      fetchQRCodes();
    } catch (error) {
      console.error('Error moving QR code:', error);
      toast.error('Failed to move QR code');
    } finally {
      setIsMoving(false);
    }
  };

  // Group QR codes by folder
  const groupedQRCodes = () => {
    const grouped = {};
    const noFolder = [];

    qrCodes.forEach(qrCode => {
      if (qrCode.folder_id) {
        const folderId = qrCode.folder_id;
        if (!grouped[folderId]) {
          grouped[folderId] = {
            folder: folders.find(f => f.id === folderId) || { id: folderId, name: qrCode.folder_name || 'Unknown Folder', color: qrCode.folder_color || '#0ea5e9' },
            qrCodes: []
          };
        }
        grouped[folderId].qrCodes.push(qrCode);
      } else {
        noFolder.push(qrCode);
      }
    });

    return { grouped, noFolder };
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>Loading QR codes...</div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader>
        <PageTitle>
          <FiRefreshCw />
          My QR Codes
        </PageTitle>
        <CreateButton to="/qr-generator">
          <FiPlus />
          Create New QR Code
        </CreateButton>
      </PageHeader>

      {qrCodes.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No QR codes yet</EmptyStateTitle>
          <EmptyStateDescription>
            Create your first QR code to get started
          </EmptyStateDescription>
          <CreateButton to="/qr-generator">
            <FiPlus />
            Create Your First QR Code
          </CreateButton>
        </EmptyState>
      ) : (() => {
        const { grouped, noFolder } = groupedQRCodes();
        return (
          <>
            {/* Display QR codes grouped by folders */}
            {Object.values(grouped).map(({ folder, qrCodes: folderQRCodes }) => (
              <div key={folder.id} style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '12px',
                  padding: '10px 14px',
                  background: 'white',
                  borderRadius: '6px',
                  border: `2px solid ${folder.color || '#0ea5e9'}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    borderRadius: '3px', 
                    background: folder.color || '#0ea5e9' 
                  }} />
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                    {folder.name}
                  </h3>
                  <span style={{ color: '#6b7280', fontSize: '11px' }}>
                    ({folderQRCodes.length} QR code{folderQRCodes.length !== 1 ? 's' : ''})
                  </span>
                </div>
                <QRCodeGrid>
                  {folderQRCodes.map((qrCode) => (
                    <QRCodeCard key={qrCode.id}>
                      <QRCodeImage src={qrCode.qr_code_image} alt={qrCode.title} />
                      <QRCodeTitle>{qrCode.title}</QRCodeTitle>
                      {qrCode.description && (
                        <QRCodeDescription>{qrCode.description}</QRCodeDescription>
                      )}
                      <QRCodeMeta>
                        <span>Type: {qrCode.qr_type}</span>
                        <span>•</span>
                        <span>Created: {formatDate(qrCode.created_at)}</span>
                      </QRCodeMeta>
                      <QRCodeActions>
                        <ActionButton onClick={() => handleEdit(qrCode)}>
                          <FiEdit />
                          Update Link
                        </ActionButton>
                        <ActionButton onClick={() => handleMove(qrCode)}>
                          Move
                        </ActionButton>
                        <ActionButton onClick={() => downloadQRCode(qrCode)}>
                          <FiDownload />
                          Download
                        </ActionButton>
                        <ActionButton onClick={() => copyQRCode(qrCode)}>
                          <FiCopy />
                          Copy
                        </ActionButton>
                        <ActionButton onClick={() => handleDelete(qrCode.id, qrCode.title)}>
                          <FiTrash2 />
                          Delete
                        </ActionButton>
                      </QRCodeActions>
                    </QRCodeCard>
                  ))}
                </QRCodeGrid>
              </div>
            ))}
            
            {/* Display QR codes without folders */}
            {noFolder.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '12px',
                  padding: '10px 14px',
                  background: 'white',
                  borderRadius: '6px',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                    No Folder
                  </h3>
                  <span style={{ color: '#6b7280', fontSize: '11px' }}>
                    ({noFolder.length} QR code{noFolder.length !== 1 ? 's' : ''})
                  </span>
                </div>
                <QRCodeGrid>
                  {noFolder.map((qrCode) => (
                    <QRCodeCard key={qrCode.id}>
                      <QRCodeImage src={qrCode.qr_code_image} alt={qrCode.title} />
                      <QRCodeTitle>{qrCode.title}</QRCodeTitle>
                      {qrCode.description && (
                        <QRCodeDescription>{qrCode.description}</QRCodeDescription>
                      )}
                      <QRCodeMeta>
                        <span>Type: {qrCode.qr_type}</span>
                        <span>•</span>
                        <span>Created: {formatDate(qrCode.created_at)}</span>
                      </QRCodeMeta>
                      <QRCodeActions>
                        <ActionButton onClick={() => handleEdit(qrCode)}>
                          <FiEdit />
                          Update Link
                        </ActionButton>
                        <ActionButton onClick={() => handleMove(qrCode)}>
                          Move
                        </ActionButton>
                        <ActionButton onClick={() => downloadQRCode(qrCode)}>
                          <FiDownload />
                          Download
                        </ActionButton>
                        <ActionButton onClick={() => copyQRCode(qrCode)}>
                          <FiCopy />
                          Copy
                        </ActionButton>
                        <ActionButton onClick={() => handleDelete(qrCode.id, qrCode.title)}>
                          <FiTrash2 />
                          Delete
                        </ActionButton>
                      </QRCodeActions>
                    </QRCodeCard>
                  ))}
                </QRCodeGrid>
              </div>
            )}
          </>
        );
      })()}

      {editModal.isOpen && (
        <Modal onClick={(e) => e.target === e.currentTarget && setEditModal({ isOpen: false, qrCode: null })}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Update QR Code Content</ModalTitle>
              <CloseButton onClick={() => setEditModal({ isOpen: false, qrCode: null })}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <div>
              <Label>Current Content (You can change the link/content without regenerating the QR code)</Label>
              {editModal.qrCode.qr_type === 'URL' && (
                <Input
                  type="url"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="https://example.com"
                />
              )}
              {editModal.qrCode.qr_type === 'TEXT' && (
                <TextArea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Enter text content"
                />
              )}
              {editModal.qrCode.qr_type === 'EMAIL' && (
                <Input
                  type="email"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="example@email.com"
                />
              )}
              {editModal.qrCode.qr_type === 'PHONE' && (
                <Input
                  type="tel"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="+1234567890"
                />
              )}
              {!['URL', 'TEXT', 'EMAIL', 'PHONE'].includes(editModal.qrCode.qr_type) && (
                <TextArea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Enter content"
                />
              )}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <ActionButton onClick={() => setEditModal({ isOpen: false, qrCode: null })}>
                  Cancel
                </ActionButton>
                <ActionButton onClick={handleUpdateContent} primary disabled={isUpdating}>
                  {isUpdating ? 'Updating...' : 'Update Content'}
                </ActionButton>
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}

      {moveModal.isOpen && (
        <Modal onClick={(e) => e.target === e.currentTarget && setMoveModal({ isOpen: false, qrCode: null })}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Move QR Code to Folder</ModalTitle>
              <CloseButton onClick={() => setMoveModal({ isOpen: false, qrCode: null })}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <div>
              <Label>Select Folder</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <ActionButton 
                  onClick={() => handleMoveToFolder(null)}
                  disabled={isMoving}
                  style={{ 
                    width: '100%', 
                    justifyContent: 'flex-start',
                    background: moveModal.qrCode?.folder_id === null ? '#f3f4f6' : 'white'
                  }}
                >
                  No Folder
                </ActionButton>
                {folders.map((folder) => (
                  <ActionButton
                    key={folder.id}
                    onClick={() => handleMoveToFolder(folder.id)}
                    disabled={isMoving}
                    style={{ 
                      width: '100%', 
                      justifyContent: 'flex-start',
                      background: moveModal.qrCode?.folder_id === folder.id ? '#f3f4f6' : 'white'
                    }}
                  >
                    <div style={{ 
                      width: '12px', 
                      height: '12px', 
                      borderRadius: '3px', 
                      background: folder.color || '#0ea5e9',
                      marginRight: '8px'
                    }} />
                    {folder.name}
                  </ActionButton>
                ))}
              </div>
              {folders.length === 0 && (
                <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>
                  No folders available. Create folders from the Dashboard to organize your QR codes.
                </div>
              )}
            </div>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default QRCodeManager;

